import PropTypes from 'prop-types';

import {Box, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {NotImplemented} from '@amblique/contentful/app/components/contentful/not-implemented';
import {SplitPanel5050} from '@amblique/king-living/app/contentful/models/section-panel/split-panel-5050';
import {SplitPanel6040} from '@amblique/king-living/app/contentful/models/section-panel/split-panel-6040';
import {SplitPanel6535} from '@amblique/king-living/app/contentful/models/section-panel/split-panel-6535';
import {ModularPreview} from '@amblique/king-living/app/contentful/models/section-panel/modular-preview';
import {SupportBlock} from '@amblique/king-living/app/contentful/models/section-panel/support-block';
import {EditorialFeatureBlockCF020} from '@amblique/king-living/app/contentful/models/section-panel/editorial-feature-block-cf020';
import {EditorialCopyCF021} from '@amblique/king-living/app/contentful/models/section-panel/editorial-copy-cf021';
import {RangeCategoryTileGridBlockCF030} from '@amblique/king-living/app/contentful/models/section-panel/range-category-tile-grid-block-cf030';
import {FormBlockCF058} from '@amblique/king-living/app/contentful/models/section-panel/form-block-cf058';
import {SplitHeading} from '@amblique/king-living/app/contentful/models/section-panel/split-heading';

/**
 * SectionPanel component
 *
 * @param {Object} props - Component props
 * @param {Contentful.SectionPanel} props.component - SectionPanel component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement}
 */
export const SectionPanel = ({component, isComplete = true, isFetching = false, ...props}) => {
  const {appearance} = component;
  const styles = useMultiStyleConfig('contentful/section-panel', {
    component,
    isComplete,
    isFetching,
    variant: appearance,
  });

  return (
    <Box {...styles.root} {...props}>
      {appearance === 'Split Heading' ? (
        <SplitHeading component={component} isComplete={isComplete} isFetching={isFetching} styles={styles} />
      ) : appearance === '50/50' ? (
        <SplitPanel5050 component={component} isComplete={isComplete} isFetching={isFetching} styles={styles} />
      ) : appearance === '60/40' ? (
        <SplitPanel6040 component={component} isComplete={isComplete} isFetching={isFetching} styles={styles} />
      ) : appearance === '65/35' ? (
        <SplitPanel6535 component={component} isComplete={isComplete} isFetching={isFetching} styles={styles} />
      ) : appearance === 'King Difference Modular preview Block - (CF-013)' ? (
        <ModularPreview component={component} isComplete={isComplete} isFetching={isFetching} styles={styles} />
      ) : appearance === 'Support Block - (CF-019)' ? (
        <SupportBlock component={component} isComplete={isComplete} isFetching={isFetching} styles={styles} />
      ) : appearance === 'Editorial Feature Block - (CF-020) [1]' ||
        appearance === 'Editorial Feature Block - (CF-020) [2]' ||
        appearance === 'Editorial Feature Block - (CF-020) [3]' ? (
          <EditorialFeatureBlockCF020 component={component} isComplete={isComplete} isFetching={isFetching} styles={styles} />
        ) : appearance === 'Editorial Copy - (CF-021)' ? (
          <EditorialCopyCF021 component={component} isComplete={isComplete} isFetching={isFetching} styles={styles} />
        ) : appearance === 'Range/Category Tile Grid Block - (CF-030) [1]' ||
          appearance === 'Range/Category Tile Grid Block - (CF-030) [2]' ? (
            <RangeCategoryTileGridBlockCF030 component={component} isComplete={isComplete} isFetching={isFetching} styles={styles} />
          ) : appearance === 'Form Block - (CF-058)' ? (
            <FormBlockCF058 component={component} isComplete={isComplete} isFetching={isFetching} styles={styles} />
          ) : (
            <NotImplemented component={component} isComplete={isComplete} isFetching={isFetching} />
          )}
    </Box>
  );
};

SectionPanel.displayName = 'SectionPanel';
SectionPanel.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
